import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiClientOptions, ApiErrorKind, ServerErrorPayload } from './api-client.type';

// 도메인 계약(라이선스/entitlement) 미러 — 두 앱 공유(§1)
export * from './contracts/license';
// 구독 해지 계약 미러 — 접수 대장/상태 배지 공유(§7.8)
export * from './contracts/subscription-cancellation';
// 교육 도메인 어휘(enum·라벨) — web 접수 화면과 admin-web 운영 화면이 공유
export * from './contracts/education';
// 전역 에러 알림 판정 — 두 앱의 QueryClient 가 같은 규칙을 쓴다
export * from './error-notice';
// 리다이렉트 착지 화면의 1회 실행 스토어 — 결제 결과 화면이 공유한다
export * from './redirect-run';
// 에러 본문 — 폼이 검증 실패 칸을 표시할 때 ApiError.detail 을 이 타입으로 읽는다
export type { ServerErrorPayload, ServerFieldError } from './api-client.type';

// ===== HTTP 상태코드 (의미 부여용 named const) =====
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

/**
 * refresh 실패가 '세션 무효'인지 '일시 장애'인지 판정.
 * 401/403 만 세션 무효 — 그 외(네트워크·타임아웃·5xx)는 재시도 가능한 장애이므로
 * 쿠키·토큰을 지우지 않는다.
 */
function isSessionRejected(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN;
}

/**
 * refresh 를 **탭 간에** 직렬화한다.
 *
 * `isRefreshing` 플래그는 탭 하나 안에서만 유효하다. 탭이 3개면 access 만료 시점에
 * refresh 도 3발이 동시에 나가고, 서버는 rotation 을 하나만 성공시키므로 나머지는
 * 재사용 탐지로 흘러 세션이 통째로 끊길 수 있다. Web Locks 로 한 번에 하나만 보낸다 —
 * 뒤에 락을 잡은 탭은 이미 회전된 새 쿠키로 요청하므로 정상 회전이 된다.
 *
 * Web Locks 가 없는 환경(구형 브라우저·SSR·테스트)에서는 그대로 실행한다.
 *
 * ⚠️ 쿠키 모드 전용이다. bearer 모드는 요청 body 를 락 **밖에서** 만들기 때문에,
 * 대기하다 락을 잡은 쪽은 이미 회전된 옛 토큰을 보내게 된다(쿠키는 브라우저가
 * 전송 시점의 최신 값을 붙여줘서 이 문제가 없다).
 *
 * 락 이름에 refreshUrl 을 넣는 이유: Web Locks 는 **origin 단위**라, web 과 admin-web 이
 * 같은 origin 에 서빙되면 서로 다른 인증 체계인데도 같은 락을 두고 경합한다.
 * 엔드포인트별로 분리하면 그런 간섭이 없다.
 */
function refreshLockName(refreshUrl: string): string {
  return `platform-auth-refresh:${refreshUrl}`;
}

function withRefreshLock<T>(
  enabled: boolean,
  refreshUrl: string,
  run: () => Promise<T>,
): Promise<T> {
  // Web Locks 는 secure context 전용이라 http 스테이징에서는 아예 없다.
  // 'in' 검사만으로는 "있는데 undefined" 인 구현을 못 거른다 — 값까지 확인한다.
  if (!enabled || typeof navigator === 'undefined' || !navigator.locks) {
    return run();
  }
  // lib.dom 의 request() 반환 타입이 Promise<any> 라 호출부로 any 가 새지 않게 좁힌다.
  return navigator.locks.request(refreshLockName(refreshUrl), run) as Promise<T>;
}

export class ApiError<TDetail = unknown> extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly code: string;
  readonly detail?: TDetail;

  constructor(
    kind: ApiErrorKind,
    status: number,
    code: string,
    message: string,
    detail?: TDetail,
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

function pickMessage(data: ServerErrorPayload | undefined, fallback: string): string {
  if (!data?.message) return fallback;
  if (Array.isArray(data.message)) return data.message.join('\n');
  return data.message;
}

function normalizeError(error: unknown): ApiError {
  if (axios.isCancel(error)) {
    const message = error instanceof Error ? error.message : 'Request canceled';
    return new ApiError('CANCELED', 0, 'CANCELED', message);
  }

  if (axios.isAxiosError<ServerErrorPayload>(error)) {
    if (error.code === 'ECONNABORTED') {
      return new ApiError('TIMEOUT', 0, 'TIMEOUT', error.message || 'Request timeout');
    }
    if (!error.response) {
      return new ApiError('NETWORK', 0, 'NETWORK_ERROR', error.message || 'Network error');
    }
    const { status, data } = error.response;
    return new ApiError(
      'SERVER',
      status,
      data?.code ?? 'UNKNOWN',
      pickMessage(data, error.message),
      data,
    );
  }

  const message = error instanceof Error ? error.message : 'Unknown error';
  return new ApiError('UNKNOWN', 0, 'UNKNOWN', message);
}

/**
 * AxiosRequestConfig 확장.
 *
 * 호출부에서 `skipAuthRedirect: true` 를 넘기면 401 발생 시
 * refresh 시도/onUnauthorized 호출 모두 건너뛰고 그냥 401 을 reject 한다.
 *
 * 사용처: 비로그인 상태에서도 호출 가능한 "선택적 인증" API.
 *   예) 헤더의 장바구니 카운트, store 페이지에서 보여주는 비로그인 카탈로그.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

// ===== 팩토리 =====
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const {
    baseURL,
    timeout = 15000,
    tokenStorage,
    refreshUrl,
    onUnauthorized,
    headers,
    authMode = 'bearer',
    hasAuthHint,
    onServerError,
  } = options;

  const instance = axios.create({
    baseURL,
    timeout,
    headers: { 'Content-Type': 'application/json', ...headers },
    withCredentials: authMode === 'cookie',
  });

  if (authMode === 'bearer') {
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = tokenStorage?.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  let isRefreshing = false;
  let waitQueue: Array<(token: string | null) => void> = [];

  const flushQueue = (token: string | null) => {
    const callbacks = waitQueue;
    waitQueue = [];
    callbacks.forEach((cb) => cb(token));
  };

  /** 정규화 + 서버 에러 훅. 훅이 던져도 원래 에러 흐름은 깨지지 않게 한다. */
  const rejectWithHook = (error: unknown) => {
    const normalized = normalizeError(error);
    if (normalized.kind === 'SERVER' && onServerError) {
      try {
        onServerError({
          status: normalized.status,
          code: normalized.code,
          detail: normalized.detail,
        });
      } catch {
        // 훅 오류는 무시 — 호출부는 원래 에러를 받아야 한다.
      }
    }
    return Promise.reject(normalized);
  };

  instance.interceptors.response.use(
    (res) => res,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        return rejectWithHook(error);
      }

      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & {
            _retry?: boolean;
            skipAuthRedirect?: boolean;
          })
        | undefined;
      const status = error.response?.status ?? 0;

      // ── 비로그인이거나 호출부가 명시적으로 refresh를 거부한 요청 ─────
      // refresh 시도/리다이렉트 모두 건너뛰고 그냥 reject 한다.
      const isOptionalAuthCall = !!originalRequest?.skipAuthRedirect;
      const noAuthHint = !!hasAuthHint && !hasAuthHint();
      if (status === HTTP_UNAUTHORIZED && (isOptionalAuthCall || noAuthHint)) {
        return rejectWithHook(error);
      }

      const canRefresh =
        status === HTTP_UNAUTHORIZED &&
        !!refreshUrl &&
        !!originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes(refreshUrl);

      if (canRefresh) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            waitQueue.push((newToken) => {
              if (authMode === 'bearer') {
                if (!newToken) return reject(normalizeError(error));
                originalRequest.headers!.Authorization = `Bearer ${newToken}`;
              } else if (newToken === null) {
                return reject(normalizeError(error));
              }
              resolve(instance(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const body =
            authMode === 'bearer'
              ? { refreshToken: tokenStorage?.getRefreshToken() }
              : undefined;

          // 인스턴스가 아닌 전역 axios 라 timeout 이 상속되지 않는다. 명시하지 않으면
          // refresh 가 응답 없이 매달릴 때 isRefreshing 이 영영 풀리지 않아
          // 이후 모든 401 재시도가 큐에 갇힌다.
          const { data } = await withRefreshLock(authMode === 'cookie', refreshUrl, () =>
            axios.post<{ accessToken: string; refreshToken?: string }>(
              `${baseURL}${refreshUrl}`,
              body,
              { withCredentials: authMode === 'cookie', timeout },
            ),
          );

          tokenStorage?.setTokens(data);
          flushQueue(data.accessToken ?? 'ok');

          if (authMode === 'bearer') {
            originalRequest.headers!.Authorization = `Bearer ${data.accessToken}`;
          }
          return instance(originalRequest);
        } catch (refreshError) {
          flushQueue(null);
          // 세션을 버리는 건 서버가 '이 자격증명은 무효'라고 답했을 때뿐이다.
          // 네트워크 단절·타임아웃·5xx 까지 로그아웃으로 처리하면 롤링 배포 중
          // refresh 한 번 실패한 사용자가 그대로 로그인 화면으로 튕긴다.
          if (isSessionRejected(refreshError)) {
            tokenStorage?.clear();
            onUnauthorized?.();
          }
          return Promise.reject(normalizeError(refreshError));
        } finally {
          isRefreshing = false;
        }
      }

      return rejectWithHook(error);
    },
  );

  return instance;
}
